import merge from 'lodash/merge'
import isNil from 'lodash/isNil'
import React, { useContext as useContextCore } from 'react'
import type { DocumentNode } from 'graphql/language'
import { orderComponents } from '../../components/Order/orderComponents'
import type { UseDocumentResult } from '@ztechapps/common.hook'
import type { Order } from '@ztechapps/common.contract'
import { isOrderStatusEditable, isPaymentOutstanding, isPostShipmentPayable } from '@ztechapps/order.common'
import { useOrder } from '../../hooks/useOrder'

export type ContextProps = {
  children?: React.ReactNode
  canCancel?: boolean
  canEdit?: boolean
  headerContent?: string
  showChat?: boolean
  filter?: any
  sort?: any
  query?: DocumentNode
  components?: any
}

export type ContextType = {
  orderId?: string
  shippingDate?: string
  canEditUsingStatus: boolean
  isStillNeedsPayment: boolean
  isPostShipmentPayable: boolean
} & Omit<ContextProps, 'children'> &
  UseDocumentResult<Order>

const context = React.createContext<ContextType>({} as ContextType)
const CoreProvider = context.Provider

export const Provider = ({
  children,
  canCancel = true,
  canEdit,
  headerContent,
  filter,
  sort,
  query,
  components,
  ...props
}: ContextProps) => {
  const docResult = useOrder({ filter, sort, query })

  const shippingDate = docResult?.document?.shipping?.date
  const status = docResult?.document?.status
  const orderId = docResult?.document?._id
  const paymentStatus = docResult?.document?.paymentStatus
  //merge the components with the default
  components = merge({}, orderComponents, components)

  const canEditUsingStatus = isOrderStatusEditable(status)

  const isStillNeedsPayment = isPaymentOutstanding(paymentStatus)

  //if canEdit  was passed then re-used it
  canEdit = !isNil(canEdit) ? canEdit : canEditUsingStatus

  //if no header content was specified then use the status
  headerContent = headerContent ?? status

  return (
    <CoreProvider
      value={{
        ...props,
        orderId,
        canCancel,
        canEdit,
        headerContent,
        components,
        shippingDate,
        ...docResult,
        canEditUsingStatus,
        isStillNeedsPayment,
        isPostShipmentPayable: isPostShipmentPayable(status, paymentStatus),
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useContext = () => useContextCore(context)
